import React, { useEffect, useState, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faTimes, faClock, faKey, faRotate, faClipboardList, faHandshake,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { CopyField, CredentialsCopyBlock } from '../../components/shared';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

const REQUEST_STATUS_MAP: Record<string, StatusDef> = {
  pending:  { bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-700 dark:text-amber-400',   icon: faClock, label: 'Pending'  },
  approved: { bg: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-400',   icon: faCheck, label: 'Approved' },
  rejected: { bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-400',       icon: faTimes, label: 'Rejected' },
};

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

interface MerchantRequest {
  id: string;
  reseller_id: string;
  merchant_name: string;
  company_name: string;
  email: string;
  phone: string;
  fee_percentage: number;
  status: string;
  admin_note: string;
  approved_by: string;
  approved_at: string;
  created_at: string;
  resellers: {
    id: string;
    name: string;
    email: string;
    fee_rate: number;
  };
}

interface ApprovalResult {
  merchant: {
    name: string;
    email: string;
    fee_percentage: number;
  };
  temp_password: string;
}

const MerchantRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<MerchantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<MerchantRequest | null>(null);
  const [approvePassword, setApprovePassword] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [approvalResult, setApprovalResult] = useState<ApprovalResult | null>(null);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await merchantApi.get('/admin/resellers/merchant-requests');
      setRequests(response.data.data || []);
    } catch {
      toast.error('ไม่สามารถโหลดคำขอ Merchant ได้');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessingId(selectedRequest.id);
    try {
      const response = await merchantApi.post(`/admin/resellers/merchant-requests/${selectedRequest.id}/approve`, {
        password: approvePassword || undefined,
        admin_note: adminNote || undefined,
      });
      toast.success('สร้าง Merchant สำเร็จ!');
      setApprovalResult(response.data.data);
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอนุมัติ');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !adminNote.trim()) return;
    setProcessingId(selectedRequest.id);
    try {
      await merchantApi.post(`/admin/resellers/merchant-requests/${selectedRequest.id}/reject`, {
        admin_note: adminNote,
      });
      toast.success('ปฏิเสธคำขอแล้ว');
      closeRejectModal();
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการปฏิเสธ');
    } finally {
      setProcessingId(null);
    }
  };

  const openApproveModal = (req: MerchantRequest) => {
    setSelectedRequest(req);
    setApprovePassword('');
    setAdminNote('');
    setApprovalResult(null);
    setShowApproveModal(true);
  };

  const openRejectModal = (req: MerchantRequest) => {
    setSelectedRequest(req);
    setAdminNote('');
    setShowRejectModal(true);
  };

  const closeApproveModal = () => {
    if (processingId) return;
    setShowApproveModal(false);
    setSelectedRequest(null);
    setApprovalResult(null);
    setApprovePassword('');
    setAdminNote('');
  };

  const closeRejectModal = () => {
    if (processingId) return;
    setShowRejectModal(false);
    setSelectedRequest(null);
    setAdminNote('');
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const approvedCount = requests.filter((r) => r.status === 'approved').length;
  const rejectedCount = requests.filter((r) => r.status === 'rejected').length;

  const filtered = useMemo(
    () => (filterStatus === 'all' ? requests : requests.filter((r) => r.status === filterStatus)),
    [requests, filterStatus],
  );

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faClipboardList,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'ทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{requests.length.toLocaleString()}</span>,
    },
    {
      icon: faClock,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'รออนุมัติ',
      value: <span className="text-amber-600 dark:text-amber-400">{pendingCount.toLocaleString()}</span>,
    },
    {
      icon: faCheck,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'อนุมัติแล้ว',
      value: <span className="text-emerald-600 dark:text-emerald-400">{approvedCount.toLocaleString()}</span>,
    },
    {
      icon: faTimes,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ปฏิเสธ',
      value: <span className="text-red-600 dark:text-red-400">{rejectedCount.toLocaleString()}</span>,
    },
  ], [requests.length, pendingCount, approvedCount, rejectedCount]);

  const columns: Column<MerchantRequest>[] = useMemo(() => [
    {
      key: 'merchant_name',
      header: 'Merchant',
      sortable: true,
      render: (req) => (
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate">{req.merchant_name}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{req.email}</p>
          {req.company_name && (
            <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate">{req.company_name}</p>
          )}
        </div>
      ),
    },
    {
      key: 'resellers',
      header: 'Reseller',
      sortable: true,
      render: (req) => (
        <div className="min-w-0">
          <p className="text-[13px] text-gray-700 dark:text-slate-200 truncate">{req.resellers?.name || '-'}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{req.resellers?.email || ''}</p>
        </div>
      ),
    },
    {
      key: 'fee_percentage',
      header: 'Fee %',
      sortable: true,
      width: '80px',
      render: (req) => (
        <span className="text-[13px] font-semibold text-gray-800 dark:text-slate-100">{req.fee_percentage}%</span>
      ),
    },
    {
      key: 'reseller_rate',
      header: 'Reseller Rate',
      width: '100px',
      render: (req) => (
        <span className="text-[13px] font-medium text-orange-600 dark:text-orange-400">
          {req.resellers?.fee_rate || 0}%
        </span>
      ),
    },
    {
      key: 'commission',
      header: 'Commission',
      width: '100px',
      render: (req) => (
        <span className="text-[13px] font-semibold text-emerald-600 dark:text-emerald-400">
          {(req.fee_percentage - (req.resellers?.fee_rate || 0)).toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      sortable: true,
      width: '110px',
      render: (req) => (
        <StatusBadge status={req.status} statusMap={REQUEST_STATUS_MAP} />
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      sortable: true,
      width: '90px',
      render: (req) => (
        <span className="text-[12px] text-gray-400 dark:text-slate-500">
          {req.created_at ? new Date(req.created_at).toLocaleDateString('th-TH') : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '160px',
      render: (req) => req.status === 'pending' ? (
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openApproveModal(req); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
            อนุมัติ
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openRejectModal(req); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
            ปฏิเสธ
          </button>
        </div>
      ) : (
        <span className="text-[11px] text-gray-400 dark:text-slate-500">
          {req.approved_by ? `โดย ${req.approved_by}` : '-'}
        </span>
      ),
    },
  ], []);

  const handleFilterReset = () => setFilterStatus('all');

  const loginUrl = process.env.NODE_ENV === 'production'
    ? `${window.location.origin.replace(/^(https?:\/\/)[^.]+\./, '$1merchant.')}/login`
    : `${window.location.origin}/login`;

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faClipboardList}
        title="Merchant Requests"
        subtitle={`อนุมัติหรือปฏิเสธคำขอเปิด Merchant จาก Reseller • ${requests.length.toLocaleString()} รายการ`}
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      <UiFilterPanel
        onSearch={() => {}}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadRequests()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="w-fit shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <div className="inline-flex items-stretch h-10 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit">
            {([
              { key: 'all' as StatusFilter, label: 'ทั้งหมด', count: requests.length },
              { key: 'pending' as StatusFilter, label: 'รออนุมัติ', count: pendingCount },
              { key: 'approved' as StatusFilter, label: 'อนุมัติ', count: approvedCount },
              { key: 'rejected' as StatusFilter, label: 'ปฏิเสธ', count: rejectedCount },
            ]).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilterStatus(tab.key)}
                className={`inline-flex items-center gap-1.5 px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200
                  ${filterStatus === tab.key
                    ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm'
                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                  }`}
              >
                {tab.label}
                <span className={`px-1.5 min-w-[1.25rem] text-center rounded-full text-xs font-bold leading-none py-0.5
                  ${filterStatus === tab.key
                    ? 'bg-gray-100 dark:bg-slate-500 text-gray-600 dark:text-slate-200'
                    : 'bg-gray-200/60 dark:bg-slate-600/60 text-gray-400 dark:text-slate-500'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </UiFilterPanel>

      <DataTable
        data={filtered}
        columns={columns}
        loading={loading}
        keyExtractor={(req) => req.id}
        emptyMessage={filterStatus !== 'all' ? 'ไม่พบคำขอในสถานะนี้' : 'ยังไม่มีคำขอ Merchant'}
        emptyIcon={<FontAwesomeIcon icon={faClipboardList} className="text-5xl text-gray-200 dark:text-slate-600" />}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
      />

      <UiModal
        open={showApproveModal && !!selectedRequest}
        onClose={closeApproveModal}
        title={approvalResult ? 'Merchant สร้างสำเร็จ!' : 'อนุมัติคำขอ Merchant'}
        icon={approvalResult ? faCheck : faHandshake}
        iconColor={approvalResult ? 'text-emerald-600' : undefined}
        iconStyle={approvalResult ? undefined : { color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          approvalResult ? (
            <UiButton variant="secondary" onClick={closeApproveModal} className="w-full justify-center">
              ปิด
            </UiButton>
          ) : (
            <div className="flex gap-3">
              <UiButton
                variant="secondary"
                onClick={closeApproveModal}
                disabled={!!processingId}
                className="flex-1 justify-center"
              >
                ยกเลิก
              </UiButton>
              <UiButton
                loading={!!processingId}
                onClick={handleApprove}
                className="flex-1 justify-center"
              >
                อนุมัติ & สร้าง Merchant
              </UiButton>
            </div>
          )
        }
      >
        {approvalResult ? (
          <>
            <div className="text-center -mt-2 mb-2">
              <p className="text-xs text-gray-400 dark:text-slate-500">{approvalResult.merchant.name}</p>
              <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
                Fee: {approvalResult.merchant.fee_percentage}%
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 ring-1 ring-blue-100 dark:ring-blue-800 space-y-2">
              <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faKey} className="text-[10px]" />
                ข้อมูลแยกฟิลด์
              </h4>
              <CopyField label="URL Login" value={loginUrl} className="text-sm" />
              <CopyField label="User" value={approvalResult.merchant.email} className="text-sm" />
              <CopyField label="Password" value={approvalResult.temp_password} className="text-sm" />
              <p className="text-[11px] text-gray-500 dark:text-slate-400 italic mt-2">
                หมายเหตุ: Merchant ต้องเข้าไปสร้าง API Key เองในหน้า API Keys หลัง login
              </p>
            </div>
            <div className="bg-white dark:bg-slate-700/50 rounded-xl p-4 ring-1 ring-gray-200 dark:ring-slate-600 space-y-2">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide mb-1">
                ข้อความสำหรับส่งให้ลูกค้า
              </h4>
              <CredentialsCopyBlock
                credentials={{
                  loginUrl,
                  user: approvalResult.merchant.email,
                  password: approvalResult.temp_password,
                  merchantName: approvalResult.merchant.name,
                }}
              />
            </div>
          </>
        ) : selectedRequest && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-1.5 text-sm">
              <p className="text-gray-800 dark:text-slate-100">
                <span className="text-gray-500 dark:text-slate-400">Merchant:</span>{' '}
                <strong>{selectedRequest.merchant_name}</strong>
              </p>
              <p className="text-gray-700 dark:text-slate-200">
                <span className="text-gray-500 dark:text-slate-400">Email:</span> {selectedRequest.email}
              </p>
              <p className="text-gray-700 dark:text-slate-200">
                <span className="text-gray-500 dark:text-slate-400">Fee:</span> {selectedRequest.fee_percentage}%
              </p>
              <p className="text-gray-700 dark:text-slate-200">
                <span className="text-gray-500 dark:text-slate-400">Reseller:</span>{' '}
                {selectedRequest.resellers?.name} ({selectedRequest.resellers?.fee_rate}%)
              </p>
              <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                Commission: {(selectedRequest.fee_percentage - (selectedRequest.resellers?.fee_rate || 0)).toFixed(2)}%
              </p>
            </div>
            <div className="space-y-4 mt-4">
              <div>
                <label className={LABEL_CLASS}>รหัสผ่าน Merchant</label>
                <input
                  type="text"
                  value={approvePassword}
                  onChange={(e) => setApprovePassword(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="เว้นว่างเพื่อสร้างอัตโนมัติ"
                />
                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">เว้นว่างถ้าต้องการให้ระบบสร้างรหัสผ่านให้</p>
              </div>
              <div>
                <label className={LABEL_CLASS}>หมายเหตุ Admin</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  className={INPUT_CLASS}
                  rows={2}
                  placeholder="ไม่บังคับ"
                />
              </div>
            </div>
          </>
        )}
      </UiModal>

      <UiModal
        open={showRejectModal && !!selectedRequest}
        onClose={closeRejectModal}
        title="ปฏิเสธคำขอ Merchant"
        icon={faTimes}
        iconColor="text-red-500"
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton
              variant="secondary"
              onClick={closeRejectModal}
              disabled={!!processingId}
              className="flex-1 justify-center"
            >
              ยกเลิก
            </UiButton>
            <UiButton
              variant="danger"
              loading={!!processingId}
              disabled={!adminNote.trim()}
              onClick={handleReject}
              className="flex-1 justify-center"
            >
              ปฏิเสธ
            </UiButton>
          </div>
        }
      >
        {selectedRequest && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-1.5 text-sm">
              <p className="text-gray-800 dark:text-slate-100">
                <span className="text-gray-500 dark:text-slate-400">Merchant:</span>{' '}
                <strong>{selectedRequest.merchant_name}</strong>
              </p>
              <p className="text-gray-700 dark:text-slate-200">
                <span className="text-gray-500 dark:text-slate-400">Reseller:</span> {selectedRequest.resellers?.name}
              </p>
            </div>
            <div className="mt-4">
              <label className={LABEL_CLASS}>เหตุผล *</label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                required
                className={INPUT_CLASS}
                rows={3}
                placeholder="ระบุเหตุผลในการปฏิเสธ..."
              />
            </div>
          </>
        )}
      </UiModal>
    </div>
  );
};

export default MerchantRequestsPage;
